"use client";

import { useRef, useEffect, useMemo } from "react";
import { ChatMessage, hasSendEmailQuestion } from "./messages/ChatMessage";
import { isEmailMessageSent } from "@/lib/savePrompt";
import { WelcomeScreen } from "./messages/WelcomeScreen";
import { TypingIndicator } from "./messages/TypingIndicator";
import { Attachment } from "@/lib/api";

interface Message {
  id: string;
  role: "user" | "assistant";
  content: string;
  timestamp: Date;
  attachments?: Attachment[];
}

interface ChatMessagesContainerProps {
  messages: Message[];
  isTyping: boolean;
  onSendMessage: (message: string) => void;
  disabled?: boolean;
  chatId?: string;
  sessionId?: string;
  streamingMessageId?: string;
  awaitingStreamingRevealMessageId?: string;
  onStreamingMessageFirstReveal?: (messageId: string) => void;
  // From GET /api/chats/{id}: the chat has a sent study email.
  emailSent?: boolean;
  // Messages whose email card was sent on this page.
  sentEmailMessageIds?: string[];
  onEmailSent?: (messageId: string) => void;
}

const NO_SENT_EMAILS: string[] = [];

/**
 * ChatMessagesContainer - The scrollable messages area
 * This container handles:
 * - Scrollable message list with proper overflow
 * - Auto-scroll to bottom on new messages
 * - Welcome screen when no messages
 * - Independent scrolling within fixed bounds
 */
export function ChatMessagesContainer({
  messages,
  isTyping,
  onSendMessage,
  disabled = false,
  chatId,
  sessionId,
  streamingMessageId,
  awaitingStreamingRevealMessageId,
  onStreamingMessageFirstReveal,
  emailSent = false,
  sentEmailMessageIds = NO_SENT_EMAILS,
  onEmailSent,
}: ChatMessagesContainerProps) {
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const lastEmailMessageId = useMemo(() => {
    if (!emailSent) return undefined;
    for (let i = messages.length - 1; i >= 0; i--) {
      const message = messages[i];
      if (message.role === "assistant" && hasSendEmailQuestion(message.content)) {
        return message.id;
      }
    }
    return undefined;
  }, [messages, emailSent]);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages, isTyping]);

  return (
    <div
      ref={scrollContainerRef}
      className="flex-1 min-h-0 overflow-y-auto bg-transparent scrollbar-chat"
      style={{
        // Use stable, well-supported properties
        overflowY: 'auto',
        WebkitOverflowScrolling: 'touch', // For iOS momentum scrolling
        // Simple height that works everywhere
        height: '100%',
      }}
    >
      <div className="mx-auto max-w-3xl px-4 lg:px-8">
        {/* Fixed padding values that work across all browsers */}
        <div className="pt-14 lg:pt-4">
          {messages.length === 0 ? (
            <WelcomeScreen onSendMessage={onSendMessage} disabled={disabled} />
          ) : (
            <div className="space-y-8">
              {messages.map((message) => (
                <ChatMessage
                  key={message.id}
                  message={message}
                  chatId={chatId}
                  sessionId={sessionId}
                  isStreamingMessage={message.id === streamingMessageId}
                  onFirstStreamingReveal={
                    message.id === awaitingStreamingRevealMessageId
                      ? () => onStreamingMessageFirstReveal?.(message.id)
                      : undefined
                  }
                  emailSent={isEmailMessageSent({
                    messageId: message.id,
                    lastEmailMessageId,
                    chatEmailSent: emailSent,
                    sentMessageIds: sentEmailMessageIds,
                  })}
                  onEmailSent={() => onEmailSent?.(message.id)}
                />
              ))}
              {isTyping && <TypingIndicator />}
              <div ref={messagesEndRef} />
            </div>
          )}
          {/* Simple bottom padding for input area */}
          <div className="h-32 lg:h-8" />
        </div>
      </div>
    </div>
  );
}
